import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Switch } from "@/components/Switch";
import { Select } from "@/components/Select";

import type { ListPreferences } from "@/components/listPreferences";

interface ListSettingsProps {
    columns: {
        key: string;
        label: string;
        sortable?: boolean;
        sortKey?: string;
    }[];
    value: ListPreferences;
    onChange: (value: ListPreferences) => void;
    inline?: boolean;
    sort?: { key: string; order: "asc" | "desc" };
    onSortChange?: (sort: { key: string; order: "asc" | "desc" }) => void;
}

export const ListSettings = ({
    columns,
    value,
    onChange,
    inline = false,
    sort,
    onSortChange,
}: ListSettingsProps) => {
    const { t } = useTranslation("global");
    const [open, setOpen] = useState(false);
    const visibleCount = columns.filter(
        (column) => !value.hidden.includes(column.key)
    ).length;
    const toggle = (key: string, checked: boolean): void => {
        onChange({
            ...value,
            hidden: checked
                ? value.hidden.filter((entry) => entry !== key)
                : [...value.hidden, key],
        });
    };
    const content = (
        <div className="space-y-5">
            {sort &&
                onSortChange &&
                columns.some((column) => column.sortable !== false) && (
                    <div className="space-y-3">
                        <Select<string>
                            className="w-full"
                            label={t("workspace.list.sort_by")}
                            value={sort.key}
                            onChange={(key) => onSortChange({ ...sort, key })}
                            options={columns
                                .filter((column) => column.sortable !== false)
                                .map((column) => ({
                                    value: column.sortKey ?? column.key,
                                    label: column.label,
                                }))}
                        />
                        <Select<string>
                            className="w-full"
                            label={t("workspace.list.sort_order")}
                            value={sort.order}
                            onChange={(order) =>
                                onSortChange({
                                    ...sort,
                                    order: order === "desc" ? "desc" : "asc",
                                })
                            }
                            options={[
                                {
                                    value: "asc",
                                    label: t("workspace.list.ascending"),
                                },
                                {
                                    value: "desc",
                                    label: t("workspace.list.descending"),
                                },
                            ]}
                        />
                    </div>
                )}
            <Select<string>
                className="w-full"
                label={t("workspace.list.density")}
                value={value.density}
                onChange={(density) =>
                    onChange({
                        ...value,
                        density:
                            density === "compact" ? "compact" : "comfortable",
                    })
                }
                options={["comfortable", "compact"].map((density) => ({
                    value: density,
                    label: t(`workspace.list.${density}`),
                }))}
            />
            <div className="space-y-3">
                <p className="text-sm font-semibold">
                    {t("workspace.list.columns")}
                </p>
                {columns.map((column) => (
                    <div key={column.key} className="text-sm">
                        <Switch
                            id={`column-${column.key}`}
                            label={column.label}
                            className="!flex !w-full !justify-between"
                            checked={!value.hidden.includes(column.key)}
                            onChange={(checked) => toggle(column.key, checked)}
                            disabled={
                                visibleCount <= 1 &&
                                !value.hidden.includes(column.key)
                            }
                        />
                    </div>
                ))}
            </div>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => onChange({ density: "comfortable", hidden: [] })}
            >
                {t("workspace.list.reset_columns")}
            </Button>
        </div>
    );
    if (inline) {
        return content;
    }
    return (
        <>
            <Button
                variant="outline"
                size="sm"
                icon="settings"
                onClick={() => setOpen(true)}
            >
                {t("workspace.list.configure")}
            </Button>
            <Modal
                isOpen={open}
                onClose={() => setOpen(false)}
                title={t("workspace.list.configure")}
                subtitle={t("workspace.list.preferences_hint")}
                maxWidth="sm"
                hideFullscreen
                footer={
                    <Button variant="primary" onClick={() => setOpen(false)}>
                        {t("common.close")}
                    </Button>
                }
            >
                {content}
            </Modal>
        </>
    );
};
